import { motion } from 'motion/react';
import { BarChart3, Clock, Target, TrendingUp, Zap, Lock, Sparkles, ArrowUpRight } from 'lucide-react';
import { useState, useEffect } from 'react';

export function Analytics() {
  const [counts, setCounts] = useState({
    drafts: 0,
    inserted: 0,
    timeSaved: 0,
    weeklyDrafts: 0
  });

  // Animated counter effect
  useEffect(() => {
    const duration = 2000; // 2 seconds
    const steps = 60;
    const interval = duration / steps;

    const targets = {
      drafts: 34,
      inserted: 0,
      timeSaved: 0,
      weeklyDrafts: 34
    };

    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;

      setCounts({
        drafts: Math.floor(targets.drafts * progress),
        inserted: Math.floor(targets.inserted * progress),
        timeSaved: Math.floor(targets.timeSaved * progress),
        weeklyDrafts: Math.floor(targets.weeklyDrafts * progress)
      });

      if (currentStep >= steps) {
        clearInterval(timer);
        setCounts(targets);
      }
    }, interval);

    return () => clearInterval(timer);
  }, []);

  const metrics = [
    {
      icon: Zap,
      label: 'Total Drafts Generated',
      value: counts.drafts.toLocaleString(),
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-500/10',
      iconColor: 'text-blue-500'
    },
    {
      icon: Target,
      label: 'Insert Rate',
      value: '0%',
      subtext: '0 drafts inserted',
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-500/10',
      iconColor: 'text-purple-500'
    },
    {
      icon: Clock,
      label: 'Time Saved',
      value: `~${counts.timeSaved} min`,
      subtext: 'Average 5 min per draft',
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-500/10',
      iconColor: 'text-green-500'
    },
    {
      icon: TrendingUp,
      label: 'This Week',
      value: `${counts.weeklyDrafts} drafts`,
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-500/10',
      iconColor: 'text-orange-500'
    },
    {
      icon: BarChart3,
      label: 'Top Role',
      value: 'Generic',
      subtext: '100% usage',
      color: 'from-pink-500 to-pink-600',
      bgColor: 'bg-pink-500/10',
      iconColor: 'text-pink-500'
    },
    {
      icon: Sparkles,
      label: 'Feedback Score',
      value: '100%',
      subtext: 'All drafts helpful',
      color: 'from-cyan-500 to-cyan-600',
      bgColor: 'bg-cyan-500/10',
      iconColor: 'text-cyan-500'
    }
  ];

  const comingSoonFeatures = [
    {
      icon: TrendingUp,
      title: 'Response Rate Tracking',
      description: 'See which emails get replies and optimize your approach'
    },
    {
      icon: BarChart3,
      title: 'A/B Testing',
      description: 'Compare different tones and styles to find what works best'
    },
    {
      icon: Target,
      title: 'Conversion Analytics',
      description: 'Track how AI-drafted emails impact your sales pipeline'
    },
    {
      icon: Sparkles,
      title: 'Team Benchmarks',
      description: 'Compare performance across your organization (Enterprise)'
    }
  ];

  return (
    <section id="analytics" className="py-20 lg:py-32 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#60A5FA]/10 rounded-full text-[#60A5FA] text-sm font-medium mb-6">
            <BarChart3 size={16} />
            Built-in Analytics
          </div>
          
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Track Your Impact,<br />
            <span className="text-[#60A5FA]">Measure Your ROI</span>
          </h2>
          
          <p className="text-xl text-white/90 max-w-3xl mx-auto mb-4">
            Every draft, every save, every minute reclaimed — tracked in real-time with built-in analytics.
          </p>
          
          <div className="flex items-center justify-center gap-2 text-sm text-white/70">
            <Lock size={14} />
            <span>100% private — all metrics stored locally on your device</span>
          </div>
        </motion.div>

        {/* Metrics Dashboard Preview */}
        <motion.div
          className="mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="relative p-8 lg:p-12 bg-white/60 dark:bg-[#1E3A8A]/10 backdrop-blur-sm rounded-3xl border-2 border-gray-200 dark:border-[#1E3A8A]/30 shadow-2xl">
            {/* Decorative corner accent */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#60A5FA]/20 to-transparent rounded-bl-full" />
            
            <div className="relative">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                    Your Analytics Dashboard
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Real-time insights from your extension
                  </p>
                </div>
                <div className="hidden sm:flex items-center gap-2 px-4 py-2 bg-green-500/10 text-green-600 dark:text-green-400 rounded-lg text-sm font-medium">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  Live Data
                </div>
              </div>

              {/* Metrics Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {metrics.map((metric, index) => (
                  <motion.div
                    key={metric.label}
                    className="relative p-6 bg-white dark:bg-[#0F172A]/60 rounded-2xl border-2 border-gray-100 dark:border-[#1E3A8A]/20 shadow-lg hover:shadow-xl transition-all group"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                    whileHover={{ y: -5 }}
                  >
                    {/* Icon */}
                    <div className={`w-12 h-12 ${metric.bgColor} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <metric.icon size={24} className={metric.iconColor} />
                    </div>

                    {/* Label */}
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      {metric.label}
                    </div>

                    {/* Value */}
                    <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                      {metric.value}
                    </div>

                    {/* Subtext */}
                    {metric.subtext && (
                      <div className="text-xs text-gray-500 dark:text-gray-500">
                        {metric.subtext}
                      </div>
                    )}

                    {/* Gradient accent line */}
                    <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${metric.color} rounded-b-xl opacity-0 group-hover:opacity-100 transition-opacity`} />
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Value Propositions */}
        <motion.div
          className="grid md:grid-cols-3 gap-8 mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="text-center p-8 bg-white/40 dark:bg-[#1E3A8A]/5 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-[#1E3A8A]/20">
            <div className="w-16 h-16 bg-gradient-to-br from-[#60A5FA] to-[#1E3A8A] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-[#60A5FA]/30">
              <Clock size={32} className="text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              Reclaim Your Time
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Average users save <strong>8+ hours per week</strong> on email communication
            </p>
          </div>

          <div className="text-center p-8 bg-white/40 dark:bg-[#1E3A8A]/5 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-[#1E3A8A]/20">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-purple-500/30">
              <Target size={32} className="text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              Data-Driven Insights
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Discover which roles, tones, and styles work best for your needs
            </p>
          </div>

          <div className="text-center p-8 bg-white/40 dark:bg-[#1E3A8A]/5 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-[#1E3A8A]/20">
            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-500/30">
              <Lock size={32} className="text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              100% Private
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              All analytics stored locally — your data never leaves your device
            </p>
          </div>
        </motion.div>

        {/* Coming Soon Section */}
        <motion.div
          className="relative"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <div className="relative p-8 lg:p-12 bg-gradient-to-br from-[#1E3A8A] via-[#1E40AF] to-[#60A5FA] rounded-3xl shadow-2xl overflow-hidden">
            {/* Decorative pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
            </div>

            <div className="relative">
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium mb-4">
                  <Sparkles size={16} />
                  Coming Soon
                </div>
                <h3 className="text-3xl sm:text-4xl font-bold text-white mb-3">
                  Advanced Analytics on the Horizon
                </h3>
                <p className="text-xl text-white/80 max-w-2xl mx-auto">
                  We're building even more powerful insights to supercharge your email productivity
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {comingSoonFeatures.map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    className="p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 hover:bg-white/15 transition-all group"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.6 + index * 0.1 }}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                        <feature.icon size={24} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                          {feature.title}
                          <ArrowUpRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                        </h4>
                        <p className="text-white/70 text-sm">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}